import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faDownload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { useUser } from '../../contexts/UserContext';
import { useFileUpload } from '../../hooks/useFileUpload';
import {
  useDeleteClaimFile,
  useGetPresignedClaimFile,
  useToggleFileLock,
  useEditClaimFile,
  downloadAllClaimFiles,
} from '../../services/claims';

import ListView from './components/ListView';
import RenameFileModal from './components/RenameFileModal';
import IngestionStatusAlert from './components/IngestionStatusAlert';
import {
  FILE_BROWSER_FOLDERS,
  DBQ_FILES_UI_FOLDERS,
  getFileFolderPath,
  getPacketKey,
} from './fileFolderUtils';

// Feature flag: Set to true to show Pinecone ingestion status alert in Files tab
// This allows admins to see which files need indexing and trigger backfill
const SHOW_INGESTION_STATUS_UI = true;

const FileBrowser = ({ claimId, files, onRefresh }) => {
  const { user, token, hasPermission, hasAnyPermission } = useUser();
  const fileRef = useRef();
  const [selFileId, setSelFileId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [selectedFileForRename, setSelectedFileForRename] = useState(null);

  const { busy, current, total, startUpload } = useFileUpload(claimId, () => {
    fileRef.current.value = '';

    if (typeof onRefresh === 'function') {
      onRefresh();
    }
  });

  const delFile = useDeleteClaimFile(() => {
    toast.success('The file was successfully deleted.');
    if (typeof onRefresh === 'function') onRefresh();
  });

  const toggleFileLock = useToggleFileLock((res) => {
    toast.success(`The file was successfully ${res.fileLocked ? 'locked' : 'unlocked'}.`);
    if (typeof onRefresh === 'function') onRefresh();
  });

  const moveFile = useEditClaimFile(() => {
    toast.success('The file was successfully moved.');
    if (typeof onRefresh === 'function') onRefresh();
  });

  const openFile = useGetPresignedClaimFile((res) => {
    window.open(res.url, '_blank');
  });

  // Permission-based checks for file operations
  const canViewAdminFolders = hasAnyPermission(['files:view_all', 'files:manage']);
  const canDownloadAll = hasAnyPermission(['files:view_all', 'files:manage']);
  const canViewIngestionStatus = hasPermission('admin:casegpt_content');
  const isClient = user?.userType === 'client' || user?.userType === 'pending_client';

  const FOLDER_LIST = useMemo(() => {
    const base = FILE_BROWSER_FOLDERS.filter(
      (f) => typeof f.role === 'undefined' || (f.role === 'admin' && canViewAdminFolders),
    );
    // DBQ Previews + Exam Packets are internal-only — hide from clients.
    if (isClient) return base;
    return [...base, ...DBQ_FILES_UI_FOLDERS];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, canViewAdminFolders, isClient]);

  const folderListWithCounts = useMemo(() => {
    return FOLDER_LIST.map((folder) => {
      const folderFiles = files.filter(
        (file) => getFileFolderPath(file, FOLDER_LIST) === folder.path,
      );
      // For packet-grouped folders, count unique packets (same key used by
      // PacketGroupList so the badge matches the rendered group count).
      const count =
        folder.groupBy === 'packet'
          ? new Set(folderFiles.map(getPacketKey).filter(Boolean)).size
          : folderFiles.length;
      return { ...folder, count };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, claimId, FOLDER_LIST]);

  useEffect(() => {
    if (selFileId) {
      fileRef.current.click();
    }
  }, [selFileId]);

  const handleFileMove = (file, targetFolderId) => {
    // targetFolderId comes from ListView as the folder path (e.g., "evidence", "statements")
    const fileDir = targetFolderId || 'root';

    moveFile.mutate({
      claimId,
      fileId: file.fileId,
      fileDir,
    });
  };

  const handleViewParsedContent = (file) => {
    const url = `/admin/claim/${claimId}/files/${file.fileId}/content`;
    window.open(url, '_blank');
  };

  return (
    <>
      <input
        type="file"
        multiple={!selFileId}
        ref={fileRef}
        onChange={async (e) => {
          if (!e.target.files || e.target.files.length === 0) return;
          const folder = null;
          if (selFileId) {
            const { uploaded } = await startUpload(e.target.files[0], selFileId, folder);
            setSelFileId(null);
            if (uploaded) toast.success('File replaced successfully');
          } else {
            const { uploaded, failed } = await startUpload(e.target.files, null, folder);
            if (uploaded && !failed) {
              toast.success(uploaded === 1 ? 'File uploaded successfully' : `${uploaded} files uploaded successfully`);
            } else if (uploaded && failed) {
              toast.warning(`${uploaded} uploaded, ${failed} failed`);
            }
          }
        }}
        style={{ display: 'none' }}
      />
      <div className="d-flex justify-content-end mb-3">
        {canDownloadAll && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={async () => {
              setDownloadingAll(true);
              await downloadAllClaimFiles(claimId, token);
              setDownloadingAll(false);
            }}
            disabled={downloadingAll}
            className="me-2"
          >
            <FontAwesomeIcon icon={faDownload} />{' '}
            {downloadingAll ? 'Preparing files...' : 'Download All'}
          </Button>
        )}
        <Button
          type="primary"
          size="sm"
          onClick={() => fileRef.current.click()}
        >
          <FontAwesomeIcon icon={faFileUpload} /> Upload New File(s)
        </Button>
      </div>

      {/* Show ingestion status alert (controlled by SHOW_INGESTION_STATUS_UI flag and permission) */}
      {SHOW_INGESTION_STATUS_UI && canViewIngestionStatus && (
        <IngestionStatusAlert claimId={claimId} />
      )}

      <hr />

      {busy ? (
        <div className="mb-3 text-center">
          <strong>Uploading{total > 1 ? ` ${current} of ${total}` : ''}...</strong>
        </div>
      ) : null}

      <ListView
        folders={folderListWithCounts}
        files={files}
        onFileOpen={(file) => {
          openFile.mutate({ claimId, fileId: file.fileId });
        }}
        onFileReplace={(file) => {
          setSelFileId(file.fileId);
        }}
        onFileRename={(file) => {
          setSelectedFileForRename(file);
          setShowRenameModal(true);
        }}
        onFileDelete={(file) => {
          if (window.confirm('Are you sure you want to delete this file?')) {
            delFile.mutate({ claimId, fileId: file.fileId });
          }
        }}
        onFileToggleLock={(file) => {
          toggleFileLock.mutate({ claimId, fileId: file.fileId });
        }}
        onViewParsedContent={handleViewParsedContent}
        onFileMove={handleFileMove}
      />

      <RenameFileModal
        show={showRenameModal}
        toggle={() => setShowRenameModal(false)}
        file={selectedFileForRename}
        claimId={claimId}
        onSuccess={() => {
          toast.success('The file was successfully renamed.');
          if (typeof onRefresh === 'function') onRefresh();
        }}
      />
    </>
  );
};

export default FileBrowser;
